import type { FeitosDoComece } from '../utils/comeceAqui';
import { duplicataPossivel } from '../utils/duplicata';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { Transaction } from '../types/Transaction';
import { ProjectionResponse } from '../types/Projection';
import { PaymentMethod, ProgramaDePontos } from '../types/PaymentMethod';
import { EstadoDaMoeda } from '../types/Moeda';
import { Reminder } from '../types/Reminder';
import { Investment } from '../types/Investment';
import { Category, Essencial } from '../types/Category';
import { Goal, CreateGoalDto } from '../types/Goal';
import { supabase } from '../supabase';
import { SESSAO_FALSA_ATIVA, cenario, adminFalso } from '../dev/sessaoFalsa';
import {
  MODO_DEMO, PERGUNTAS_DA_DEMO, EVENTO_DE_ESCRITA_NA_DEMO, EVENTO_DE_LANCAMENTO_NA_DEMO,
  LANCAMENTOS_DA_DEMO, ehLancamentoNaDemo, MotivoDoAviso,
} from '../demo/modo';
import { EM_DESENVOLVIMENTO, atrasoLocalMs, capturarLentoDaUrl } from '../dev/atrasoLocal';

/** O plano venceu com o app aberto: o servidor respondeu 402 `sem-plano`. */
export const EVENTO_SEM_PLANO = 'fintracking:sem-plano';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

/*
 * `config.params` entra na URL antes de chegar ao roteador.
 *
 * O axios só junta os dois no adapter de verdade, então quem passa mês por
 * `{ params: { month } }` — e são três rotas — chegava sem o mês às respostas
 * locais. O efeito era silencioso e convincente: o card respondia com o mês
 * corrente sob o título do mês navegado, ou seja o rótulo mentindo.
 */
const comParams = (url: string, params: any): string => {
  if (!params) return url;
  const q = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return q ? `${url}${url.includes('?') ? '&' : '?'}${q}` : url;
};

/*
 * ── O modo demonstração (`/demo`) ────────────────────────────────────────
 *
 * Quem abre a demonstração vê o app com os dados de uma pessoa inventada. A
 * regra que não se quebra: NENHUMA requisição sai. Por isso é o `adapter` que
 * é trocado — ele É quem faz a chamada; interceptor não impede que ela saia.
 *
 * Leitura: responde com `demo/dados.ts`, buscado sob demanda — quem não abre a
 * demonstração não baixa os dados.
 *
 * Escrita: recusada AQUI, antes de qualquer coisa, com um 403 local e um
 * evento que faz o app mostrar "na demonstração nada é gravado". A lista do
 * que passa é fechada (`PERGUNTAS_DA_DEMO`): uma rota nova que grava nasce
 * barrada, em vez de nascer fingindo que gravou.
 *
 * A exceção é o lançamento (28/10): `LANCAMENTOS_DA_DEMO` por aba, contados
 * aqui. Ele vai para a memória de `demo/dados.ts`, não para a rede, e a
 * projeção passa a contá-lo. O seguinte é recusado como qualquer escrita.
 *
 * Desenho: `docs/designs/modo-demonstracao.md`.
 */
if (MODO_DEMO) {
  let lancadosNaDemo = 0;
  api.defaults.adapter = async (config) => {
    const metodo = (config.method ?? 'get').toLowerCase();
    const url = comParams(config.url ?? '', config.params);
    const rota = url.split('?')[0].replace(/^\//, '');
    const lancamento = ehLancamentoNaDemo(metodo, rota);
    const lancamentoLivre = lancamento && lancadosNaDemo < LANCAMENTOS_DA_DEMO;

    if (metodo !== 'get' && !PERGUNTAS_DA_DEMO.includes(rota) && !lancamentoLivre) {
      const motivo: MotivoDoAviso = lancamento ? 'outro-lancamento' : 'escrita';
      try { window.dispatchEvent(new CustomEvent(EVENTO_DE_ESCRITA_NA_DEMO, { detail: motivo })); } catch { /* sem janela, sem aviso */ }
      const e: any = new Error('Request failed with status code 403');
      e.isAxiosError = true;
      e.config = config;
      e.response = {
        data: {
          error: motivo === 'outro-lancamento'
            ? 'Na demonstração dá para fazer um lançamento. Crie a sua conta para lançar quantos quiser.'
            : 'Na demonstração nada é gravado. Crie a sua conta para lançar de verdade.',
        },
        status: 403, statusText: 'Forbidden (demonstração)', headers: {}, config,
      };
      throw e;
    }

    // Contado ANTES de esperar: dois envios seguidos não passam os dois.
    if (lancamentoLivre) lancadosNaDemo += 1;
    const dados = await import('../demo/dados');
    dados.definirContexto({ demo: true });
    const data = dados.respostaDeMentira(metodo, url, config.data);
    if (lancamentoLivre) {
      try { window.dispatchEvent(new CustomEvent(EVENTO_DE_LANCAMENTO_NA_DEMO)); } catch { /* sem janela, sem nota */ }
    }
    return {
      data,
      status: lancamentoLivre ? 201 : 200,
      statusText: 'OK (demonstração)',
      headers: {},
      config,
    } as any;
  };
}

/*
 * A sessão de desenvolvimento responde AQUI, e a rede nem é tocada.
 *
 * Trocar o `adapter` é o ponto certo: interceptor de requisição não consegue
 * impedir a chamada de sair, e interceptor de resposta chega tarde demais. O
 * adapter É quem faz a chamada, então substituí-lo é o único jeito de garantir
 * que nada vai para o servidor.
 *
 * Em produção `SESSAO_FALSA_ATIVA` é uma constante falsa e este bloco sai do
 * arquivo na build — ver `dev/sessaoFalsa.ts`.
 *
 * Em `/demo` quem responde é a demonstração, mesmo em desenvolvimento: é o
 * único jeito de ver a demonstração de verdade antes de publicá-la.
 */
if (SESSAO_FALSA_ATIVA && !MODO_DEMO) {
  const falhar = (config: any, comResposta: boolean) => {
    const e: any = new Error(
      comResposta ? 'Request failed with status code 500' : 'Network Error',
    );
    e.isAxiosError = true;
    e.config = config;
    if (comResposta) {
      e.response = {
        data: { error: 'Não consegui gravar agora. O banco recusou a operação.' },
        status: 500,
        statusText: 'Internal Server Error',
        headers: {},
        config,
      };
    }
    return e;
  };

  api.defaults.adapter = async (config) => {
    const c = cenario();
    const metodo = (config.method ?? 'get').toLowerCase();
    if (c === 'dormindo') throw falhar(config, false);
    if (c === 'erro' && metodo !== 'get') throw falhar(config, true);
    // Sob demanda, como na demonstração: nenhum arquivo importa os dados de
    // forma estática — ver `demo/dados.ts`.
    const dados = await import('../demo/dados');
    dados.definirContexto({ cenario: c, admin: adminFalso() });
    return {
      data: dados.respostaDeMentira(metodo, comParams(config.url ?? '', config.params), config.data),
      status: 200,
      statusText: 'OK (sessão de desenvolvimento)',
      headers: {},
      config,
    } as any;
  };
}

/*
 * A espera de DESENVOLVIMENTO — a que deixa olhar a tela de carga.
 *
 * ── Por que um interceptor, e não o adapter da sessão falsa ─────────────
 *
 * Ela morava lá, e por isso só funcionava com `REACT_APP_SESSAO_FALSA=1`.
 * Quem roda `npm start` normal não tinha como segurar a tela — e foi esse o
 * relato, duas vezes seguidas. Interceptor de requisição roda ANTES do
 * adapter, qualquer que seja ele: serve para a sessão falsa e para o backend
 * de verdade, com o mesmo código.
 *
 * ── Isto não existe em produção ────────────────────────────────────────
 *
 * `EM_DESENVOLVIMENTO` é `process.env.NODE_ENV !== 'production'`, que o CRA
 * troca por literal na build: o `if` vira `false` e o bloco sai no
 * minificador. `carregamentoLocal.test.ts` prende essa condição.
 */
if (EM_DESENVOLVIMENTO) {
  capturarLentoDaUrl();
  api.interceptors.request.use(async (config) => {
    const ms = atrasoLocalMs();
    if (ms > 0) await new Promise(r => setTimeout(r, ms));
    return config;
  });
}

// Attach Supabase JWT to every request
api.interceptors.request.use(async (config) => {
  if (SESSAO_FALSA_ATIVA) return config;   // não há sessão real de onde tirar token
  // Na demonstração não se pergunta NADA ao serviço de login: nem a sessão de
  // quem já tem conta, que continua intacta do lado de fora.
  if (MODO_DEMO) return config;
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Sign out automatically on 401 (expired or invalid token)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      await supabase.auth.signOut();
    }
    // Sem plano (20/10): o plano venceu com o app aberto. O portão do plano
    // (`PortaoDoPlano`) escuta e troca o app pela tela de planos.
    if (error?.response?.status === 402 && error?.response?.data?.motivo === 'sem-plano') {
      window.dispatchEvent(new CustomEvent(EVENTO_SEM_PLANO));
    }
    return Promise.reject(error);
  }
);

export const getTransactions = async (): Promise<Transaction[]> => {
  const response = await api.get<Transaction[]>('/transactions');
  return response.data;
};

export const getTransaction = async (id: string): Promise<Transaction> => {
  const response = await api.get<Transaction>(`/transactions/${id}`);
  return response.data;
};

export const createTransaction = async (
  data: Omit<Transaction, 'id' | 'createdAt'>,
  /**
   * `verificarDuplicata`: pede ao servidor que confira antes de gravar. Se
   * houver um lançamento parecido, nada é gravado e isto rejeita com
   * `DuplicataPossivel` — quem chamou pergunta a ela e, se for o caso, chama
   * de novo SEM a opção. Ver `utils/duplicata.ts`.
   */
  opcoes: { verificarDuplicata?: boolean } = {},
): Promise<Transaction> => {
  try {
    const response = await api.post<Transaction>(
      '/transactions', data,
      opcoes.verificarDuplicata ? { params: { verificarDuplicata: 1 } } : undefined,
    );
    return response.data;
  } catch (err) {
    // Não é falha: é o servidor perguntando. Fora do Sentry de propósito.
    const corpo = (err as { response?: { status?: number; data?: { code?: string; existente?: { frase?: string } } } })?.response;
    if (corpo?.status === 409 && corpo.data?.code === 'possivel_duplicata') {
      throw duplicataPossivel(corpo.data.existente?.frase ?? 'um lançamento parecido');
    }
    Sentry.withScope((scope) => {
      scope.setTag('operation', 'createTransaction');
      scope.setContext('transaction', {
        type: data.type,
        paymentType: data.paymentType,
        description: data.description,
        value: data.value,
        isMonthly: data.isMonthly,
        hasInstallments: data.hasInstallments,
        date: data.date,
      });
      Sentry.captureException(err);
    });
    throw err;
  }
};

export const updateTransaction = async (
  id: string,
  data: Partial<Omit<Transaction, 'id' | 'createdAt'>>
): Promise<Transaction> => {
  const response = await api.put<Transaction>(`/transactions/${id}`, data);
  return response.data;
};

export const deleteTransaction = async (id: string): Promise<void> => {
  await api.delete(`/transactions/${id}`);
};

export const getProjections = async (
  months: number = 12,
  pastMonths: number = 0
): Promise<ProjectionResponse> => {
  const response = await api.get<ProjectionResponse>(`/projections?months=${months}&pastMonths=${pastMonths}`);
  return response.data;
};

export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const response = await api.get<PaymentMethod[]>('/payment-methods');
  return response.data;
};

export const createPaymentMethod = async (
  data: { name: string; monthlyLimit?: number | null; billingDay?: number | null; closingDay?: number | null }
): Promise<PaymentMethod> => {
  const response = await api.post<PaymentMethod>('/payment-methods', data);
  return response.data;
};

export const updatePaymentMethod = async (
  id: string,
  data: { name?: string; monthlyLimit?: number | null; billingDay?: number | null; closingDay?: number | null }
): Promise<PaymentMethod> => {
  const response = await api.put<PaymentMethod>(`/payment-methods/${id}`, data);
  return response.data;
};

/**
 * `force` reatribui os lançamentos para "sem método" antes de excluir. Sem
 * ele, o servidor devolve 409 com a contagem em vez de decidir sozinho.
 */
export const deletePaymentMethod = async (id: string, force = false): Promise<void> => {
  await api.delete(`/payment-methods/${id}${force ? '?force=true' : ''}`);
};

/**
 * A marca da casa num método — as três chamadas, e por que são três.
 *
 * `escopoDoMetodo` só PERGUNTA: quantos lançamentos mudariam. É o número que a
 * confirmação mostra antes de existir botão nenhum.
 *
 * `definirEscopoDoMetodo` muda o padrão, e vale do próximo lançamento em
 * diante. Inofensiva.
 *
 * `marcarHistoricoDoMetodo` reescreve o passado — a única operação desta
 * feature que torna público o que era privado. Ela é um pedido à parte de
 * propósito: nunca um efeito colateral de mudar o padrão.
 */
export const escopoDoMetodo = async (id: string, escopo: 'casa' | 'pessoal') =>
  (await api.get<{ escopoPadrao: 'casa' | 'pessoal'; alvo: string; historico: number }>(
    `/payment-methods/${id}/escopo?escopo=${escopo}`,
  )).data;

/** A bandeira e o nível do cartão, escolhidos à mão. `null` nos dois limpa. */
export const definirCartaoDoMetodo = async (id: string, bandeira: string | null, nivel: string | null) =>
  (await api.put<PaymentMethod>(`/payment-methods/${id}/cartao`, { bandeira, nivel })).data;

/** O programa e o nível da conta do banco. `programa: null` limpa. */
export const definirContaDoMetodo = async (id: string, programa: string | null, nivel: number | null) =>
  (await api.put<PaymentMethod>(`/payment-methods/${id}/conta`, { programa, nivel })).data;

/** "Meu banco não está na lista": o pedido para incluir um programa de conta. */
export const pedirProgramaDaConta = async (banco: string, nivel: string): Promise<void> => {
  await api.post('/payment-methods/conta/pedido', { banco, nivel });
};

/** Quanto o cartão pontua. `taxa: null` limpa. */
export const definirPontosDoMetodo = async (
  id: string, dados: { taxa: number | null; moeda: 'BRL' | 'USD'; taxaAlta?: number | null; faturaMinima?: number | null },
) => (await api.put<PaymentMethod>(`/payment-methods/${id}/pontos`, dados)).data;

/** Os programas de pontos dela, com o saldo estimado. `disponivel: false` = o servidor ainda não guarda pontos. */
export const getPontos = async () =>
  (await api.get<{ disponivel: boolean; programas: ProgramaDePontos[]; outros?: { id: string; nome: string }[] }>('/payment-methods/pontos')).data;

/** O saldo que ela conferiu no programa. A estimativa recomeça daqui. */
export const salvarSaldoDePontos = async (programa: string, saldo: number, venceEm: string | null) =>
  (await api.put<{ ok: boolean }>(`/payment-methods/pontos/${programa}`, { saldo, venceEm })).data;

/* ── Outras moedas (13/10) ── */

/** As carteiras de moeda estrangeira e o modo viagem. `disponivel: false` = o servidor ainda não guarda moeda. */
export const getMoedas = async () => (await api.get<EstadoDaMoeda>('/moeda')).data;

export const comprarMoeda = async (moeda: string, quantidade: number, reais: number, data?: string) =>
  (await api.post<{ ok: boolean }>('/moeda/comprar', { moeda, quantidade, reais, data })).data;

export const venderMoeda = async (moeda: string, quantidade: number, reais: number) =>
  (await api.post<{ ok: boolean }>('/moeda/vender', { moeda, quantidade, reais })).data;

export const gastarNaMoeda = async (moeda: string, valorNaMoeda: number, descricao: string, categoryId?: string | null) =>
  (await api.post<{ ok: boolean; reais: number; resta: number }>('/moeda/gastar', { moeda, valorNaMoeda, descricao, categoryId })).data;

/** `null` volta a lançar em real. */
export const definirMoedaAtiva = async (moeda: string | null) =>
  (await api.put<{ ok: boolean; ativa: string | null }>('/moeda/modo', { moeda })).data;

export const definirEscopoDoMetodo = async (id: string, escopo: 'casa' | 'pessoal') =>
  (await api.put<{ escopoPadrao: 'casa' | 'pessoal'; historico: number }>(
    `/payment-methods/${id}/escopo`, { escopo },
  )).data;

export const marcarHistoricoDoMetodo = async (id: string, escopo: 'casa' | 'pessoal') =>
  (await api.post<{ marcadas: number; escopo: string }>(
    `/payment-methods/${id}/escopo/historico`, { escopo },
  )).data;

/* ── A casa: quem está nela, e para onde foi o dinheiro dela ────────────── */

export interface MoradorDaCasa { userId: string; nome: string; entrou: number }
export interface ItemDaCasa {
  id: string; descricao: string; valor: number; categoria: string | null; de: string;
}
export interface CategoriaDaCasa { nome: string; cor: string; total: number }

export interface VisaoDaCasa {
  casa: { id: string } | null;
  /** Por que não há casa: a feature não está no ar, ou ela não entrou em uma. */
  motivo?: 'indisponivel' | 'sem-casa';
  mes: string;
  entrou: number;
  saiu: number;
  sobrou: number;
  moradores: MoradorDaCasa[];
  maiores: ItemDaCasa[];
  categorias: CategoriaDaCasa[];
  lancamentos: number;
  quantosMoradores: number;
}

export const getCasa = async (mes?: string) =>
  (await api.get<VisaoDaCasa>(`/casa${mes ? `?mes=${mes}` : ''}`)).data;

export interface MembroDaCasa {
  id: string; eu: boolean; nome: string | null; email: string | null;
  status: 'pendente' | 'aceito' | 'recusado' | 'saiu' | 'removido';
  convidadoEm: string | null; respondidoEm: string | null;
}
export interface ConviteDeCasa {
  id: string;
  /** O primeiro nome de quem convidou, congelado no envio da linha. */
  de: string;
  /**
   * Sempre `null` hoje.
   *
   * O email de quem convidou vinha de um `JOIN users`, e `users` tem política
   * `id = eu`: quem recebe o convite não enxerga a linha de quem enviou — e
   * virar par é exatamente o que está em questão. O nome basta para decidir.
   */
  email: string | null;
  convidadoEm: string | null;
}
export interface EstadoDaCasa {
  casa: { id: string } | null;
  membros: MembroDaCasa[];
  /** Estou na casa, mas ninguém mais aceitou ainda. */
  sozinha: boolean;
  convites: ConviteDeCasa[];
  teto: number;
}

export const getGrupo = async () => (await api.get<EstadoDaCasa>('/grupo')).data;

export const convidarParaCasa = async (email: string) =>
  (await api.post<{ ok: true; acao: 'criar' | 'reenviar' }>('/grupo/convites', { email })).data;

export const responderConvite = async (id: string, resposta: 'aceitar' | 'recusar') =>
  (await api.post<{ ok: true }>(`/grupo/convites/${id}/${resposta}`)).data;

export const sairDaCasa = async () => (await api.post<{ ok: true }>('/grupo/sair')).data;

export const cancelarConvite = async (id: string) =>
  (await api.delete<{ ok: true }>(`/grupo/convites/${id}`)).data;

/* ── A meta compartilhada ─────────────────────────────────────────────────── */

export interface MembroDaMeta {
  userId: string | null;
  /** "Você" para mim mesma; o primeiro nome para os outros. */
  nome: string;
  eu: boolean;
  /**
   * `null` é **"ainda não"**, e não é o mesmo que zero.
   *
   * A tela desenha `null` como texto apagado, sem barra e sem valor. Um R$ 0
   * com nome ao lado vira placar, e a feature existe para evitar briga — a
   * decisão está inteira em `services/quemPosNoBolo.ts`, no backend.
   *
   * `valor ?? 0` em qualquer lugar da tela desfaz a decisão sem quebrar nada.
   */
  valor: number | null;
}

export interface MetaCompartilhada {
  id: string;
  nome: string;
  alvo: number;
  prazoMes: string | null;
  /** Derivado de `SUM(meta_aporte.valor)`. O backend nunca guarda um escalar. */
  total: number;
  /** Só o meu — é o que entra no MEU patrimônio. */
  meu: number;
  souDona: boolean;
  membros: MembroDaMeta[];
  convites: { id: string; email: string }[];
}

export interface ConviteDeMeta {
  id: string;
  meta: string;
  /** O primeiro nome de quem chamou, congelado no envio da linha. */
  de: string;
  convidadoEm: string | null;
}

export const getMetasCompartilhadas = async () =>
  (await api.get<{ metas: MetaCompartilhada[]; convites: ConviteDeMeta[] }>('/metas')).data;

export const criarMetaCompartilhada = async (
  dados: { nome: string; alvo: number; prazoMes?: string | null },
) => (await api.post<{ ok: true; id: string }>('/metas', dados)).data;

export const convidarParaMeta = async (metaId: string, email: string) =>
  (await api.post<{ ok: true; acao: 'criar' | 'reenviar' }>(
    `/metas/${metaId}/convites`, { email })).data;

export const cancelarConviteDaMeta = async (metaId: string, membroId: string) =>
  (await api.delete<{ ok: true }>(`/metas/${metaId}/convites/${membroId}`)).data;

export const responderConviteDaMeta = async (
  membroId: string, resposta: 'aceitar' | 'recusar',
) => (await api.post<{ ok: true }>(`/metas/convites/${membroId}/${resposta}`)).data;

export const aportarNaMeta = async (metaId: string, valor: number) =>
  (await api.post<{ ok: true; valor: number }>(`/metas/${metaId}/aportes`, { valor })).data;

export const sairDaMeta = async (metaId: string) =>
  (await api.post<{ ok: true }>(`/metas/${metaId}/sair`)).data;

export const encerrarMeta = async (metaId: string) =>
  (await api.post<{ ok: true }>(`/metas/${metaId}/encerrar`)).data;

/**
 * Entrar pelo link, depois de a conta existir. Exige login — a membresia é uma
 * linha com `user_id`, e é ela que vai receber os aportes.
 *
 * LER o convite não passa por aqui, e não é descuido: a rota pública é chamada
 * com `fetch` puro dentro de `ConvitePublico.tsx`, porque este cliente põe um
 * `Authorization` do Supabase em toda chamada e quem abre o convite talvez
 * nunca tenha ouvido falar do app. A divisão entre os dois arquivos é a mesma
 * divisão que existe no backend.
 */
export const entrarNaMetaPeloToken = async (token: string) =>
  (await api.post<{ ok: true; metaId: string }>(
    `/metas/convites/token/${token}/aceitar`)).data;

export const getReminders = async (month: string): Promise<Reminder[]> => {
  const response = await api.get<Reminder[]>(`/reminders?month=${month}`);
  return response.data;
};

export const createReminder = async (
  data: { description: string; type: 'expense' | 'income'; value?: number | null; dayOfMonth: number;
          endDate?: string | null; transactionId?: string | null; paymentMethodId?: string | null }
): Promise<Reminder> => {
  const response = await api.post<Reminder>('/reminders', data);
  return response.data;
};

/**
 * Cria um lembrete a partir de uma DESPESA, já ligado a ela.
 *
 * Só despesa: lembrete existe para não esquecer de pagar, e o servidor recusa
 * receita com 400. 409 quando o lançamento já tem lembrete.
 */
export const criarLembreteDeTransacao = async (transactionId: string): Promise<Reminder> => {
  const r = await api.post<Reminder>(`/reminders/de-transacao/${transactionId}`);
  return r.data;
};

export const updateReminder = async (
  id: string,
  data: { description?: string; type?: 'expense' | 'income'; value?: number | null; dayOfMonth?: number;
          endDate?: string | null; transactionId?: string | null; paymentMethodId?: string | null }
): Promise<Reminder> => {
  const response = await api.put<Reminder>(`/reminders/${id}`, data);
  return response.data;
};

export const deleteReminder = async (id: string): Promise<void> => {
  await api.delete(`/reminders/${id}`);
};

export const checkReminder = async (id: string, month: string): Promise<void> => {
  await api.post(`/reminders/${id}/check`, { month });
};

export const uncheckReminder = async (id: string, month: string): Promise<void> => {
  await api.delete(`/reminders/${id}/check?month=${month}`);
};

/**
 * O valor real de uma mensal num mês (15/09). Informar também CONFIRMA o mês e
 * marca o lembrete ligado. `antes` é o valor que havia sido informado, se havia.
 */
export const informarValorDoMes = async (
  transactionId: string, mes: string, value: number,
): Promise<{ ok: true; antes: number | null }> =>
  (await api.put(`/transactions/${transactionId}/meses/${mes}`, { value })).data;

/** Desfaz o valor do mês. O lembrete, se foi marcado, continua marcado. */
export const desfazerValorDoMes = async (transactionId: string, mes: string): Promise<void> => {
  await api.delete(`/transactions/${transactionId}/meses/${mes}`);
};

/** Dá a fatura do cartão naquele mês como paga (15/09). Não pede valor. */
export const marcarFaturaPaga = async (paymentMethodId: string, mes: string): Promise<void> => {
  await api.put(`/payment-methods/${paymentMethodId}/faturas/${mes}`);
};

export const desmarcarFaturaPaga = async (paymentMethodId: string, mes: string): Promise<void> => {
  await api.delete(`/payment-methods/${paymentMethodId}/faturas/${mes}`);
};

export const getCategories = async (): Promise<Category[]> => {
  const response = await api.get<Category[]>('/categories');
  return response.data;
};

export const createCategory = async (data: { name: string; color: string }): Promise<Category> => {
  const response = await api.post<Category>('/categories', data);
  return response.data;
};

export const updateCategory = async (id: string, data: { name?: string; color?: string }): Promise<Category> => {
  const response = await api.put<Category>(`/categories/${id}`, data);
  return response.data;
};

/**
 * Marca a categoria como essencial, ajustável ou "não sei". `null` desfaz.
 * Rota própria porque no `PUT` comum `null` quer dizer "não mexe".
 */
export const marcarEssencial = async (id: string, essencial: Essencial | null): Promise<Category> => {
  const response = await api.put<Category>(`/categories/${id}/essencial`, { essencial });
  return response.data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  await api.delete(`/categories/${id}`);
};

export const getInvestments = async (): Promise<Investment[]> => {
  const response = await api.get<Investment[]>('/investments');
  return response.data;
};

export const createInvestment = async (
  data: Omit<Investment, 'id' | 'createdAt'> & {
    /**
     * O dinheiro sai da conta agora? true cria também a transação de débito.
     * false é cadastro de saldo que já existia — inventar uma saída retroativa
     * aí faria o caixa despencar por um aporte que nunca aconteceu.
     */
    recordTransaction?: boolean;
    date?: string;
  }
): Promise<Investment> => {
  const response = await api.post<Investment>('/investments', data);
  return response.data;
};

export const updateInvestment = async (
  id: string,
  data: Partial<Omit<Investment, 'id' | 'createdAt'>>
): Promise<Investment> => {
  const response = await api.put<Investment>(`/investments/${id}`, data);
  return response.data;
};

export const deleteInvestment = async (id: string): Promise<void> => {
  await api.delete(`/investments/${id}`);
};

/**
 * Aporte numa aplicação existente: cria a transação de débito E aumenta o
 * saldo, numa chamada só. Diferente de `updateInvestment`, que é correção do
 * número (rendimento anotado, erro de digitação) e não move o caixa.
 */
export const contributeToInvestment = async (
  id: string, value: number, date?: string,
): Promise<Investment> => {
  const res = await api.post<Investment>(`/investments/${id}/contribute`, { value, date });
  return res.data;
};

export const getGoals = async (): Promise<Goal[]> => {
  const response = await api.get<Goal[]>('/goals');
  return response.data;
};

export const createGoal = async (data: CreateGoalDto): Promise<Goal> => {
  const response = await api.post<Goal>('/goals', data);
  return response.data;
};

/**
 * Liga ou desliga o Finn Coach numa meta. Rota própria: aceitar ser cobrada
 * não pode ir de carona numa edição de nome ou de valor.
 */
export const ligarCoach = async (id: string, ligado: boolean): Promise<Goal> => {
  const response = await api.put<Goal>(`/goals/${id}/coach`, { ligado });
  return response.data;
};

export const updateGoal = async (id: string, data: Partial<CreateGoalDto>): Promise<Goal> => {
  const response = await api.put<Goal>(`/goals/${id}`, data);
  return response.data;
};

/**
 * Exclui a meta, e opcionalmente os aportes lançados nela.
 *
 * O padrão é NÃO apagar os lançamentos. Aporte reduz o saldo em caixa (o
 * dinheiro saiu da conta), então apagá-lo devolve aquele dinheiro ao saldo —
 * é mudança de número, não faxina. Sumir com lançamento de dinheiro por conta
 * própria é a única coisa aqui que não tem desfazer.
 */
export const deleteGoal = async (
  id: string, opcoes: { apagarAportes?: boolean } = {},
): Promise<{ apagados: number }> => {
  const r = await api.delete<{ apagados: number }>(
    `/goals/${id}`,
    opcoes.apagarAportes ? { params: { aportes: 'apagar' } } : undefined,
  );
  return r.data ?? { apagados: 0 };
};

export const getPaymentMethodsSpending = async (
  year?: number,
  month?: number
): Promise<Record<string, number>> => {
  const params = new URLSearchParams();
  if (year !== undefined) params.append('year', String(year));
  if (month !== undefined) params.append('month', String(month));
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await api.get<Record<string, number>>(`/payment-methods/spending${query}`);
  return response.data;
};

export interface ScanReceiptResult {
  description: string;
  value: number;
  date: string;
  type: 'expense' | 'income';
  categoryHint: string | null;
}

export const scanReceipt = async (image: string, mimeType: string): Promise<ScanReceiptResult> => {
  const response = await api.post<ScanReceiptResult>('/scan-receipt', { image, mimeType });
  return response.data;
};

// ── Item 17: saldo âncora ────────────────────────────────────────────────
export interface BalanceAnchor {
  amount: number;
  /** YYYY-MM-DD */
  date: string;
}

export const getBalanceAnchor = async (): Promise<BalanceAnchor | null> => {
  const res = await api.get<BalanceAnchor | null>('/users/balance-anchor');
  return res.data;
};

/** `null` limpa a âncora e volta a somar todo o histórico. */
export const setBalanceAnchor = async (
  anchor: BalanceAnchor | null,
): Promise<BalanceAnchor | null> => {
  const res = await api.put<BalanceAnchor | null>(
    '/users/balance-anchor',
    anchor ?? { amount: null },
  );
  return res.data;
};

// ── Item 19: série temporal por categoria ────────────────────────────────
export interface CategorySeries {
  categoryId: string | null;
  categoryName: string;
  color: string;
  points: { month: string; total: number }[];
  total: number;
  average: number;
  trendPct: number | null;
}

export const getCategorySpend = async (
  from?: string,
  to?: string,
): Promise<{ from: string; to: string; months: string[]; series: CategorySeries[]; total: number }> => {
  const res = await api.get('/insights/category-spend', { params: { from, to } });
  return res.data;
};

// ── Item 21: simulador de cenário ────────────────────────────────────────
export const simulateProjection = async (
  scenarioTransactions: any[],
  months = 12,
): Promise<{ baseline: any; scenario: any; delta: any[] }> => {
  const res = await api.post('/projections', { scenarioTransactions, months });
  return res.data;
};

// ── Item 20: metas e investimentos na linha do tempo ─────────────────────
export interface GoalProjection {
  goalId: string;
  name: string;
  targetAmount: number;
  savedAmount: number;
  remaining: number;
  monthlyAllocation: number;
  projectedCompletionMonth: string | null;
  reason: 'already-reached' | 'no-surplus' | 'beyond-horizon' | 'deadline-passed' | null;
  /** O caixa de hoje já cobre o que falta nesta meta. */
  cashCoversThis: boolean;
  /** O prazo que ela definiu, ou `null`. */
  deadlineMonth: string | null;
  /** Quanto por mês é preciso separar para chegar no prazo. `null` sem prazo. */
  requiredMonthly: number | null;
  /** A alocação cobre o necessário. `null` sem prazo. */
  onTrackForDeadline: boolean | null;
  /** Quantos meses ainda cabem até o prazo. `null` sem prazo. */
  monthsToDeadline: number | null;
}

export interface GoalTimeline {
  /** Mediana do saldo dos meses futuros — a coluna "Saldo do Mês". */
  monthlySurplus: number;
  /** De quantos meses saiu a mediana. Sem isto ela não dá para julgar. */
  monthsConsidered: number;
  assumesFullSurplus: boolean;
  /** O mesmo número do card "Em caixa". Não entra em nenhuma conta de meta. */
  availableCash: number;
  totalRemaining: number;
  /** O caixa cobre TODAS as metas pendentes ao mesmo tempo. */
  cashCoversAll: boolean;
  goals: GoalProjection[];
  points: { month: string; cashBalance: number; investmentsTotal: number; goalsTotal: number }[];
}

export const getGoalTimeline = async (months = 24): Promise<GoalTimeline> => {
  const res = await api.get('/insights/timeline', { params: { months } });
  return res.data;
};

// ── Recomeço: a janela de análise ────────────────────────────────────────
export interface AnalysisWindowPreview {
  hiddenCount: number;
  keptCount: number;
  monthsWithData: number;
  /** `true` quando cortar aqui mata a estimativa de gasto variável. */
  losesEstimate: boolean;
}

export interface AnalysisWindow {
  analysisFrom: string | null;
  /** Meses desde o último lançamento. É o gatilho do convite. */
  staleMonths: number;
  lastMonthWithData: string | null;
  totalTransactions: number;
  /** O custo de cortar HOJE — mostrado antes de decidir, nunca depois. */
  preview: AnalysisWindowPreview;
}

export const getAnalysisWindow = async (): Promise<AnalysisWindow> =>
  (await api.get('/users/analysis-window')).data;

/** `null` limpa o corte e as análises voltam a enxergar tudo. */
export const setAnalysisWindow = async (analysisFrom: string | null): Promise<void> => {
  await api.put('/users/analysis-window', { analysisFrom });
};

/** A palavra que o SERVIDOR exige. Trocar aqui sem trocar lá não apaga nada. */
export const PALAVRA_DE_CONFIRMACAO = 'APAGAR TUDO';

/** Baixa tudo antes de apagar. A única rede de uma ação sem desfazer. */
export const exportarDados = async (): Promise<void> => {
  const { data } = await api.get('/users/export');
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fintracking-backup-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * ⚠️ IRREVERSÍVEL. Apaga lançamentos, categorias, métodos, metas,
 * investimentos, lembretes e regras aprendidas, e recadastra as categorias
 * padrão. Não existe desfazer nem backup do lado do app.
 */
export const resetarConta = async (confirm: string): Promise<void> => {
  await api.post('/users/reset', { confirm });
};

// ── O campo de análise ───────────────────────────────────────────────────
export interface SafeToSpend {
  month: string;
  income: number;
  committed: number;
  alreadySpent: number;
  /** O número. Nada de metas nem de ritmo foi subtraído dele (D4). */
  available: number;
  /** Quanto o ritmo normal ainda consome. `null` sem histórico suficiente. */
  paceRemaining: number | null;
  goalsAsk: number;
  /** `false` quando nenhuma receita cai no mês — o número não significa nada. */
  hasIncome: boolean;
  /**
   * O saldo atual: o que está DISPONÍVEL (caixa). Transferir para reserva ou
   * investimento reduz este número — o valor reaparece em `investedTotal`.
   */
  currentBalance: number;
  /** Aplicações + reservas de metas. Junto com o saldo, o total. */
  investedTotal: number;
  /** Despesa ligada a lembrete e ainda não confirmada. */
  aguardandoPagamento?: number;
  /** Receita ligada a lembrete e ainda não recebida. Ela ainda não chegou. */
  aguardandoRecebimento?: number;
  /** O caixa de quem só conta o que de fato se moveu. */
  saldoConfirmado?: number;
}

export interface CategoryBudget {
  categoryId: string;
  categoryName: string;
  month: string;
  spent: number;
  limit: number | null;
  remaining: number | null;
  typical: number | null;
  monthsUsed: number;
}

export interface PropostaDeCoach {
  categoryId: string;
  categoria: string;
  alvo: number;
  tipo: 'criar' | 'ligar';
  /** O que ela lê antes de aceitar: nome, valor e prazo. */
  texto: string;
}

/**
 * Ela disse sim à proposta de Finn Coach.
 *
 * Manda só a CATEGORIA: o servidor refaz a conta e cria com o valor dele. É a
 * única escrita que nasce no chat do site — ver `advisorSemInsight.test.ts`.
 */
export const aceitarCoach = async (
  categoryId: string,
): Promise<{ categoria: string; alvo: number; tipo: 'criar' | 'ligar' }> =>
  (await api.post('/finn/coach/aceitar', { categoryId })).data;

/**
 * A proposta de aviso que o Finn mostra (27/10): "me avisa se...".
 *
 * O texto já veio na resposta; aqui só o que o botão precisa. O `token` é a
 * proposta que o SERVIDOR montou, assinada: o botão não manda valor nenhum.
 */
export interface PropostaDeAviso {
  acao: 'criar' | 'desligar';
  texto: string;
  token: string;
  botao: string;
}

/**
 * Ela tocou em "Criar aviso" (ou "Desligar aviso"). O toque é a confirmação:
 * a leitura do modelo nunca grava sozinha — ver `advisorSemInsight.test.ts`.
 */
export const confirmarAviso = async (token: string): Promise<{ resposta: string }> =>
  (await api.post('/insights/avisos/confirmar', { token })).data;

export interface AdvisorAnswer {
  kind: 'safe-to-spend' | 'category-budget' | 'simulate' | 'query' | 'explain'
      | 'incomplete' | 'conversa';
  /**
   * Só em `conversa`: a resposta em prosa, com os números JÁ substituídos.
   *
   * O modelo escreve marcadores (`{{saldo_livre}}`) e o servidor troca pelos
   * valores calculados. Prosa com número que não veio de marcador é recusada
   * inteira lá — então o que chega aqui não tem número inventado.
   */
  texto?: string;
  /** Só em `conversa`: quais números foram citados. */
  fontes?: string[];
  /**
   * Só em `conversa`, e só quando ela pediu orientação: a proposta de Finn
   * Coach, escrita pelo servidor em código (nunca pelo modelo). Nada existe
   * até ela aceitar.
   */
  propostaDeCoach?: PropostaDeCoach;
  /** Só em `criar-aviso` (27/10): a proposta, com o botão. Nada existe até o toque. */
  propostaDeAviso?: PropostaDeAviso;
  intent: string;
  /** Nome citado que não existe no cadastro. Não some em silêncio. */
  unresolved: string | null;
  missing: string[];
  safeToSpend?: SafeToSpend;
  budget?: CategoryBudget;
  /** simulate */
  item?: string | null;
  value?: number | null;
  installments?: number | null;
  /** simulate: gasto que se repete todo mês (27/10). */
  recurring?: boolean;
  before?: SafeToSpend;
  delta?: { month: string; monthLabel: string; cumulativeCashBalance: number; baseline: number; difference: number }[];
  cashGoesNegativeAt?: string | null;
  wasNegativeBefore?: string | null;
  /** Cabe no mês? Se não, cabe no caixa? A frase vem pronta do servidor (`advisor/cabe.ts`). */
  cabe?: { peso: number; noMes: boolean; noCaixa: boolean; frase: string };
  /** O dinheiro na conta hoje, ao lado do livre do mês. */
  emCaixa?: number;
  /** "O mês está negativo, mas você tem R$ X em caixa", quando é o caso. */
  notaDoCaixa?: string | null;
  /** safe-to-spend de mês futuro: `emCaixa` é o projetado na entrada do mês. */
  futuro?: boolean;
  /** query */
  count?: number;
  total?: number;
  matchIds?: string[];
  categoryName?: string | null;
  month?: string;
  /** explain */
  explanation?: Explanation;
}

/** Sem porta de IA (D8): é aritmética, e não fica refém de uma chave de API. */
export interface SaldoDoMes {
  month: string;
  /** Caixa acumulado projetado até o fim daquele mês. */
  saldo: number;
  /** O que sobra NAQUELE mês: entra menos comprometido menos já gasto. */
  livre: number;
  hasIncome: boolean;
  /**
   * Quanto estará aplicado naquele mês. `null` no PASSADO.
   *
   * Para frente sai da projeção de patrimônio: aplicações rendendo juro
   * composto mais reservas crescendo pela sobra. Para trás não existe — o app
   * guarda o valor ATUAL de cada aplicação, não o histórico dele.
   */
  investido: number | null;
}

/**
 * Saldo e livre de um mês qualquer. `null` quando o mês está fora do
 * horizonte projetado — a faixa some a linha em vez de mostrar zero, que
 * pareceria "você não vai ter nada".
 */
export const getSaldoDoMes = async (month: string): Promise<SaldoDoMes | null> => {
  const r = await api.get<SaldoDoMes>('/insights/saldo-do-mes', { params: { month } });
  return r.status === 204 ? null : r.data;
};

export const getSafeToSpend = async (): Promise<SafeToSpend> =>
  (await api.get('/insights/safe-to-spend')).data;

/**
 * `history` carrega só a PERGUNTA e a INTENÇÃO de cada turno anterior — nunca
 * os valores. O servidor sanea de novo (`sanitizeAdvisorHistory`), porque uma
 * garantia que depende do cliente se comportar não é garantia.
 */
export interface AdvisorTurn {
  question: string;
  intent: string;
  month: string | null;
  categoryName: string | null;
}

export const askAdvisor = async (
  text: string, history: AdvisorTurn[] = [],
): Promise<{
  answer: AdvisorAnswer | null;
  problem?: string | null;
  /** Só quando o app não soube responder — ver `RespostaDoAdvisor` no servidor. */
  sugestao?: 'conector' | null;
}> => (await api.post('/insights/ask', { text, history })).data;

// ── "De onde vem esse número?" ───────────────────────────────────────────
export interface DerivationStep {
  label: string;
  value: number | null;
  kind: 'base' | 'add' | 'subtract' | 'result' | 'plain';
  hint?: string;
}

export interface Explanation {
  topic: string;
  title: string;
  /** A conta, linha a linha — montada no backend, nunca escrita pelo modelo. */
  steps: DerivationStep[];
  /** O método, em texto autoral que mora no código-fonte. */
  note: string;
  /** O que enfraquece a resposta. */
  caveats: string[];
}

export const explainInsight = async (
  text: string, scope: 'projection' | 'categories',
): Promise<{ explanation: Explanation | null; problem?: string | null; unresolved?: string | null }> =>
  (await api.post('/insights/explain', { text, scope })).data;

// ── Perguntar a lista em vez de montar o filtro ──────────────────────────
export interface QueryLabel {
  field: 'type' | 'kind' | 'category' | 'payment_method' | 'text' | 'period' | 'minValue' | 'maxValue';
  label: string;
}

export interface TransactionQueryResult {
  /** null quando não deu para entender a pergunta. */
  query: Record<string, unknown> | null;
  /** O que o filtro está fazendo, em português — vira os chips. */
  labels?: QueryLabel[];
  /** Nomes citados que não existem no cadastro. Não somem em silêncio. */
  unresolved?: string[];
  matchIds?: string[];
  count?: number;
  /**
   * Somado no BACKEND, nunca pelo modelo. Em recorte de vários meses, uma
   * parcela conta uma vez por mês — é o que ela de fato pagou no período.
   */
  total?: number;
  problem?: string | null;
}

export const queryTransactions = async (text: string): Promise<TransactionQueryResult> =>
  (await api.post('/transactions/query', { text })).data;

// ── Campo único: escreva e lance ─────────────────────────────────────────
export interface EntryGapOption {
  id: string;
  name: string;
  count: number;
  unanimous: boolean;
  matchedBy: 'description' | 'category' | 'global';
}

export interface EntryGap {
  field: 'category' | 'payment_method';
  suggestedId: string | null;
  suggestedName: string | null;
  options: EntryGapOption[];
  /** Só true quando o histórico é unânime e tem amostra suficiente. */
  canRemember: boolean;
  rememberLabel: string;
}

export interface QuickEntry {
  description: string;
  value: number;
  date: string;
  type: 'expense' | 'income';
  categoryId: string | null;
  categoryName: string | null;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  installments: number | null;
  /** Qual parcela ESTA é — o 3 de "3/6". `null` significa a primeira. */
  currentInstallment: number | null;
  /**
   * O total que ela escreveu, quando escreveu o total ("4200 em 10x"). `value`
   * já é o da parcela. Ausente num servidor antigo.
   */
  totalInformado?: number | null;
  /**
   * A data da COMPRA (13/09). `date` é o pagamento.
   *
   * A UI precisa disto para duas coisas: MOSTRAR as duas quando o cartão
   * move o pagamento, e recalcular quando o método é escolhido depois — a
   * partir da compra, nunca de `date`, senão cada recálculo soma um mês.
   */
  dataCompra?: string;
  origemDaCompra?: 'informada' | 'hoje';
  /** `'cartao'` quando o dia da fatura moveu o pagamento. Data trocada em silêncio é o erro mais caro daqui. */
  origemDoPagamento?: 'compra' | 'cartao';
  /** O dia usado, quando o cartão moveu o pagamento. */
  diaDoCartao?: number | null;
  filledByRule: { category: string | null; paymentMethod: string | null };
  gaps: EntryGap[];
  complete: boolean;
}

export const parseQuickEntry = async (
  text: string,
  /** A foto de uma lista de compras: volta um lançamento por linha. */
  imagem?: { base64: string; mimeType: string },
): Promise<{
  entries: QuickEntry[];
  /** Preenchido quando a frase era uma aplicação, não um gasto. */
  investment: ParsedInvestment | null;
  summary: string | null; provider: string; model: string;
}> => {
  const res = await api.post('/quick-entry/parse', imagem ? { text, image: imagem.base64, mimeType: imagem.mimeType } : { text });
  return res.data;
};

export const saveEntryRule = async (rule: {
  matchType: 'description' | 'category';
  matchValue: string;
  field: 'category' | 'payment_method';
  value: string;
}): Promise<void> => {
  await api.post('/quick-entry/rules', rule);
};

export interface StoredRule {
  id: string;
  matchType: 'description' | 'category';
  matchValue: string;
  field: 'category' | 'payment_method';
  value: string;
  valueName: string | null;
}

export const getEntryRules = async (): Promise<StoredRule[]> => {
  const res = await api.get<StoredRule[]>('/quick-entry/rules');
  return res.data;
};

export const deleteEntryRule = async (id: string): Promise<void> => {
  await api.delete(`/quick-entry/rules/${id}`);
};

// ── Campo único de lembrete e de meta ────────────────────────────────────
export interface ParsedReminder {
  description: string;
  dayOfMonth: number | null;
  value: number | null;
  type: 'expense' | 'income';
  missing: string[];
}

export const parseQuickReminder = async (text: string): Promise<{
  reminder: ParsedReminder | null; problem: string | null;
}> => (await api.post('/quick-entry/reminder', { text })).data;

export interface ParsedMethod {
  name: string;
  /** `null` quando ela não citou limite. O campo fica em branco no formulário. */
  monthlyLimit: number | null;
  /** `null` quando ela não citou o dia da fatura. */
  billingDay: number | null;
  /** `null` quando ela não citou o fechamento. */
  closingDay?: number | null;
}

export const parseQuickMethod = async (text: string): Promise<{
  method: ParsedMethod | null; problem: string | null;
}> => (await api.post('/quick-entry/method', { text })).data;

export interface ParsedGoal {
  type: 'reserve' | 'category_limit';
  name: string;
  targetAmount: number | null;
  savedAmount: number;
  categoryId: string | null;
  categoryName: string | null;
  /**
   * 'YYYY-MM', resolvido pelo servidor. `null` quando ela não disse prazo.
   *
   * O modelo devolve o que ela FALOU — um mês nomeado, ou uma duração — e a
   * soma de meses é feita em código (`entityPrompts.resolverPrazo`). Conta
   * feita por modelo acerta quase sempre, e o "quase" não avisa.
   */
  deadlineMonth: string | null;
  missing: string[];
}

export const parseQuickGoal = async (text: string): Promise<{
  goal: ParsedGoal | null; problem: string | null;
}> => (await api.post('/quick-entry/goal', { text })).data;

export interface InvestmentCandidate {
  id: string;
  name: string;
  type: string;
  amount: number;
  monthlyRate: number;
}

export interface ParsedInvestment {
  /** Decidido pelo backend casando o nome com o que já existe. */
  operation: 'create' | 'contribute';
  investmentId: string | null;
  /** Saldo e taxa já cadastrados, quando é aporte. */
  currentAmount: number | null;
  currentMonthlyRate: number | null;
  name: string;
  type: string;
  amount: number | null;
  /** Já em % AO MÊS. `null` quando a taxa não pôde ser convertida com certeza. */
  monthlyRate: number | null;
  rateBasis: 'annual' | 'monthly' | null;
  rateOriginal: number | null;
  /** "110% do CDI" e afins — o app não converte índice, então mostra e pergunta. */
  rateText: string | null;
  /** O dinheiro sai do caixa agora? */
  movesCash: boolean;
  ambiguous: InvestmentCandidate[];
  missing: string[];
}

export const parseQuickInvestment = async (text: string): Promise<{
  investment: ParsedInvestment | null; problem: string | null; existing?: InvestmentCandidate[];
}> => (await api.post('/quick-entry/investment', { text })).data;

/**
 * Grava o que o campo único interpretou — o mesmo caminho, venha da tela de
 * Investimentos ou do campo de lançamento.
 *
 * As três combinações são operações diferentes de propósito. Misturá-las é
 * exatamente o que fazia o caixa despencar a cada rendimento anotado:
 *
 *   aporte  + dinheiro novo -> POST /contribute : transação + saldo maior
 *   aporte  + saldo antigo  -> PUT             : só corrige o número
 *   nova    + dinheiro novo -> POST            : transação + aplicação nova
 *   nova    + saldo antigo  -> POST            : só cadastra o que já existia
 */
export const saveParsedInvestment = async (inv: ParsedInvestment): Promise<Investment> => {
  const amount = inv.amount ?? 0;
  if (inv.operation === 'contribute' && inv.investmentId) {
    if (inv.movesCash) return contributeToInvestment(inv.investmentId, amount);
    // "meu tesouro está em 620" é o saldo TOTAL, não um acréscimo.
    return updateInvestment(inv.investmentId, {
      amount,
      ...(inv.monthlyRate != null ? { monthlyRate: inv.monthlyRate } : {}),
    });
  }
  return createInvestment({
    name: inv.name,
    type: inv.type,
    amount,
    monthlyRate: inv.monthlyRate ?? 0,
    recordTransaction: inv.movesCash,
  });
};


// ── Dívidas ──────────────────────────────────────────────────────────────

export type DebtType = 'consignado' | 'pessoal' | 'financiamento' | 'cartao' | 'cheque-especial' | 'outro';

export const DEBT_TYPE_LABEL: Record<DebtType, string> = {
  consignado: 'Consignado',
  pessoal: 'Empréstimo pessoal',
  financiamento: 'Financiamento',
  cartao: 'Parcelamento de fatura',
  'cheque-especial': 'Cheque especial',
  outro: 'Outro',
};

export interface Debt {
  id: string;
  name: string;
  type: DebtType;
  installments: number;
  installmentValue: number;
  firstInstallmentDate: string;
  /** Descontado na folha: a parcela nunca aparece em Transações. */
  deductedFromSalary: boolean;
  monthlyRate: number | null;
  paidInstallments: number;
  remainingInstallments: number;
  /**
   * Quanto ainda VAI SAIR do bolso — parcelas restantes × valor.
   * Não é saldo devedor: a parcela embute juros futuros, e quitar
   * antecipado custa menos do que este número.
   */
  remainingTotal: number;
  /** 'YYYY-MM' da última parcela. */
  lastInstallmentMonth: string;
  /** A parcela em valor por dia, em frase pronta do servidor. Só se exibe. */
  porDiaTexto?: string | null;
  /** A despesa da parcela, quando ligada (16/09). Ajustada junto na quitação. */
  transactionId?: string | null;
  /** O histórico de quitações, mais recente primeiro (16/09). */
  quitacoes?: Quitacao[];
  /** Soma das economias do histórico. */
  economiaTotal?: number;
}

/** Uma quitação registrada (16/09). A foto de antes e de depois vem do servidor. */
export interface Quitacao {
  id: string;
  debtId: string;
  paidOn: string;
  amount: number;
  mode: 'parcelas' | 'valor';
  installmentsBefore: number;
  installmentsAfter: number;
  installmentValueBefore: number;
  installmentValueAfter: number;
  remainingBefore: number;
  remainingAfter: number;
  savings: number;
  sourceType: 'saldo' | 'investment' | 'goal';
  sourceId: string | null;
  sourceName: string | null;
  source: string;
  createdAt: string;
}

export type OrigemDaQuitacao =
  | { tipo: 'saldo'; metodo: string }
  | { tipo: 'investment' | 'goal'; id: string };

export interface PedidoDeQuitacao {
  valorPago: number;
  modo: 'parcelas' | 'valor';
  parcelasRestantes?: number;
  valorParcela?: number;
  origem: OrigemDaQuitacao;
}

export const quitarDivida = async (id: string, pedido: PedidoDeQuitacao): Promise<void> => {
  await api.post(`/debts/${id}/quitacoes`, pedido);
};

export const desfazerQuitacao = async (id: string, quitacaoId: string): Promise<void> => {
  await api.delete(`/debts/${id}/quitacoes/${quitacaoId}`);
};

export const ligarLancamentoDaDivida = async (id: string, transactionId: string | null): Promise<void> => {
  await api.put(`/debts/${id}/lancamento`, { transactionId });
};

export type DebtInput = Omit<Debt,
  'id' | 'paidInstallments' | 'remainingInstallments' | 'remainingTotal' | 'lastInstallmentMonth'
  | 'transactionId' | 'quitacoes' | 'economiaTotal'>;

export const getDebts = async (): Promise<Debt[]> =>
  (await api.get<Debt[]>('/debts')).data;

export const createDebt = async (data: DebtInput): Promise<Debt> =>
  (await api.post<Debt>('/debts', data)).data;

export const updateDebt = async (id: string, data: Partial<DebtInput>): Promise<Debt> =>
  (await api.put<Debt>(`/debts/${id}`, data)).data;

export const deleteDebt = async (id: string): Promise<void> => {
  await api.delete(`/debts/${id}`);
};

export interface ParsedDebt {
  name: string;
  type: DebtType;
  installments: number | null;
  installmentValue: number | null;
  /** O contratado, quando ela cita. Serve para exibir — nunca para dividir. */
  totalAmount: number | null;
  /** `null` quando o app não pode saber e precisa perguntar. */
  deductedFromSalary: boolean | null;
  monthlyRate: number | null;
  missing: string[];
}

// ── O campo único da seção Dívidas ───────────────────────────────────────

export interface DebtStatusItem {
  id: string;
  name: string;
  type: DebtType;
  installments: number;
  installmentValue: number;
  paidInstallments: number;
  remainingInstallments: number;
  /** Quanto ainda VAI SAIR do bolso. Não é o valor de quitação. */
  remainingTotal: number;
  lastInstallmentMonth: string;
  deductedFromSalary: boolean;
  /** A parcela em valor por dia, em frase pronta do servidor. Só se exibe. */
  porDiaTexto?: string | null;
}

export type DebtAnswer =
  | {
      kind: 'status';
      debts: DebtStatusItem[];
      monthlyTotal: number;
      remainingTotal: number;
      nextRelief: { name: string; month: string; monthlyGain: number } | null;
    }
  | {
      kind: 'where';
      debt: DebtStatusItem;
      deductedFromSalary: boolean;
      needsLogging: boolean;
      /** Achou um lançamento do mês com o valor exato de uma parcela em folha. */
      suspectedDoubleEntry: boolean;
    }
  | {
      kind: 'capacity';
      monthlyRoom: number;
      debts: DebtStatusItem[];
      installmentsCovered: number | null;
      target: DebtStatusItem | null;
    }
  | {
      kind: 'goal';
      name: string;
      targetAmount: number | null;
      monthlyAmount: number | null;
      monthsToTarget: number | null;
      target: DebtStatusItem | null;
      missing: string[];
    };

export const askDebt = async (text: string): Promise<{
  answer: DebtAnswer | null;
  /** Preenchido quando ela descreveu uma dívida para registrar. */
  proposal: ParsedDebt | null;
  problem: string | null;
}> => (await api.post('/debts/ask', { text })).data;

// ── WhatsApp ─────────────────────────────────────────────────────────────

/** Conta nova pede o canal; a dona libera. Ver `services/whatsapp/acesso.ts` no backend. */
export type AcessoAoWhatsapp = 'liberado' | 'pedido' | 'fechado';

export interface WhatsappStatus {
  /** Ausente num servidor antigo — e ausente lê como liberado, que era o estado de antes. */
  acesso?: AcessoAoWhatsapp;
  linked: boolean;
  /** Só o final do número — a tela não precisa do resto para confirmar qual é. */
  phoneMasked: string | null;
  linkedAt: string | null;
  /** O número DO BOT: para onde ela manda o código. `null` = não configurado. */
  botPhone: string | null;
  /** O servidor tem instância, token e segredo do webhook? */
  serverReady: boolean;
  /** `WHATSAPP_ENABLED` está ligado? Desligado, o bot recebe mas não responde. */
  sendingEnabled: boolean;
  /**
   * O canal está de pé AGORA.
   *
   * `linked` e isto são coisas diferentes, e a tela mostrava só a primeira: o
   * vínculo é nosso (fica no banco), a conexão é do Z-API com o WhatsApp. Com
   * a instância restrita, "ligado ao número …2443" continua verdade e nada
   * chega.
   */
  canal: EstadoDoCanal;
}

export interface EstadoDoCanal {
  /** `null` = a sonda nunca rodou. Diferente de 'fora-do-ar'. */
  estado: 'no-ar' | 'fora-do-ar' | null;
  desdeQuando: string | null;
  diasNoAr: number;
  diasForaDoAr: number;
  diasSemDado: number;
  /** `false` = a janela tem buraco demais para sustentar decisão. */
  medicaoConfiavel: boolean;
}

/** O estado do conector de IA: a data do último uso e o endereço para ligar. */
export interface EstadoDoConector {
  ultimoUsoEm: string | null;
  url: string | null;
  servidorPronto: boolean;
}

export const getConector = async (): Promise<EstadoDoConector> =>
  (await api.get<EstadoDoConector>('/users/conector')).data;

export const getWhatsappStatus = async (): Promise<WhatsappStatus> =>
  (await api.get<WhatsappStatus>('/users/whatsapp')).data;

export const createWhatsappCode = async (): Promise<{
  code: string; expiresAt: string; ttlMinutes: number;
}> => (await api.post('/users/whatsapp/pairing-code')).data;

/** Manda uma mensagem de teste e devolve o motivo real quando falha. */
export const pedirAcessoAoWhatsapp = async (): Promise<AcessoAoWhatsapp> =>
  (await api.post<{ acesso: AcessoAoWhatsapp }>('/users/whatsapp/pedir-acesso')).data.acesso;

export interface PedidoDeWhatsapp {
  userId: string;
  nome: string | null;
  email: string;
  pedidoEm: string | null;
}

/** Só admin. Quem pediu o WhatsApp e ainda espera. */
export const getPedidosDeWhatsapp = async (): Promise<PedidoDeWhatsapp[]> =>
  (await api.get<{ pedidos: PedidoDeWhatsapp[] }>('/monitor/whatsapp/pedidos')).data.pedidos;

/**
 * Libera e avisa a pessoa por email. `avisada: false` = liberou, mas o email
 * não saiu — a liberação vale do mesmo jeito.
 */
export const liberarWhatsapp = async (userId: string): Promise<{ avisada: boolean }> => {
  const r = (await api.post<{ ok: boolean; avisada?: boolean }>('/monitor/whatsapp/liberar', { userId })).data;
  // Servidor antigo não manda o campo, e também não mandava email.
  return { avisada: r.avisada === true };
};

export const testWhatsapp = async (): Promise<{
  ok: boolean; motivo?: string; status?: number; detalhe?: string;
}> => (await api.post('/users/whatsapp/test')).data;

export const unlinkWhatsapp = async (): Promise<void> => {
  await api.delete('/users/whatsapp');
};

export interface JanelaHabito {
  de: string; ate: string;
  /** O sinal principal: constância, não volume. */
  diasComLancamento: number;
  totalDias: number;
  lancamentos: number;
  porWhatsapp: number;
  porWeb: number;
  /** O maior silêncio dentro da janela, em dias. */
  maiorIntervalo: number;
}

export interface RetratoDoHabito {
  janelaDias: number;
  atual: JanelaHabito;
  anterior: JanelaHabito;
  tendencia: 'subindo' | 'estavel' | 'caindo' | 'sem-dado';
  diasDesdeUltimo: number | null;
  /** Sem isto o veredito é lido no escuro — ver `services/whatsapp/canal.ts`. */
  canal: Omit<EstadoDoCanal, 'desdeQuando'>;
}

export const getHabit = async (): Promise<RetratoDoHabito> =>
  (await api.get<RetratoDoHabito>('/insights/habit')).data;

/* ── Monitor ───────────────────────────────────────────────────────────── */

export type VereditoJob = 'ok' | 'atrasado' | 'falhando' | 'nunca-rodou';

export interface SaudeDeJob {
  nome: string;
  rotulo: string;
  veredito: VereditoJob;
  ultimaEm: string | null;
  ultimaOk: boolean | null;
  detalhe: string | null;
  horasDesde: number | null;
  falhasRecentes: number;
}

export interface PessoaAtiva {
  nome: string;
  ultimaAtividade: string | null;
  diasDesde: number | null;
  escritas7d: number;
}

export interface CadastroNovo {
  nome: string;
  cadastradoEm: string;
  diasDesde: number;
  /** Já escreveu alguma coisa (lançamento, meta, lembrete, investimento, dívida). */
  jaLancou: boolean;
  /** Conta demo ou a dona: não paga, fica fora do funil. */
  isenta?: boolean;
  /** A primeira etapa que falta; `null` quando fez tudo. */
  parouEm?: EtapaDoCadastro | null;
}

export type EtapaDoCadastro = 'plano' | 'pagamento' | 'banco' | 'lancamento';

export interface DegrauDoFunil {
  etapa: 'entrou' | EtapaDoCadastro;
  chegaram: number;
  /** De quantas a etapa se aplica (o banco conta só quem está no Completo). */
  de: number;
}

/**
 * Em que pé está uma sugestão.
 *
 * Era um booleano `avisado`, e ele ficou errado quando o email virou semanal:
 * uma sugestão de hoje não estava "sem aviso", estava esperando segunda.
 * Booleano não tem onde guardar essa diferença, e a tela pintava de alarme o
 * funcionamento normal.
 */
export type EstadoDaSugestao = 'no-resumo' | 'na-fila' | 'atrasada';

export interface SugestaoRecebida {
  texto: string;
  canal: string;
  quando: string;
  diasDesde: number | null;
  dePessoa: string;
  estado: EstadoDaSugestao;
}

/** O backup do banco, feito à mão por semana — ver `services/monitor/backupDoBanco.ts`. */
export interface RetratoDoBackup {
  ultimoEm: string | null;
  venceEm: string | null;
  /** Negativo = vencido há tantos dias. */
  diasParaVencer: number | null;
  pendente: boolean;
}

export interface Monitor {
  geral: 'ok' | 'atencao' | 'ruim';
  banco: { ok: boolean; latenciaMs: number | null; erro?: string };
  /** `null` quando o monitor falhou. Opcional enquanto o servidor antigo responde. */
  backup?: RetratoDoBackup | null;
  jobs: SaudeDeJob[];
  canal: Omit<EstadoDoCanal, never> | null;
  /** A transcrição de áudio tem a chave de que precisa. `null` quando o monitor falhou. */
  audio: { ouve: boolean } | null;
  usuarios: {
    total: number; ativos7d: number; ativos30d: number; pessoas: PessoaAtiva[];
    /** Contas criadas nos últimos 30 dias. Opcional enquanto o servidor antigo responde. */
    novos?: {
      semana: number; mes: number; itens: CadastroNovo[];
      /** A cobrança está ligada: as etapas do plano entram no funil. */
      comPlano?: boolean;
      funil?: DegrauDoFunil[];
    };
  } | null;
  volume7d: { total: number; porOrigem: Record<string, number> } | null;
  /**
   * O RLS está mesmo ligado, agora, nesta conexão.
   *
   * O servidor já respondia isto desde 29/08 e a tela nunca mostrou — então a
   * única pergunta que o painel existe para responder sobre o banco ficava sem
   * resposta, e quem quisesse saber tinha que ler o código.
   */
  protecao: {
    protegido: boolean;
    comPolitica: number;
    /** Tabelas sem RLS: abertas para a chave anon, que está neste JavaScript. */
    semRls: number;
    /** RLS ligado e nenhuma política: fechadas até para o app. */
    semPolitica: number;
    conexaoIgnoraRls: boolean;
    motivo: string;
  } | null;
  /**
   * `mostrando` e `teto` vêm junto porque a lista é cortada: sem eles, 50 de
   * 400 lê como "são 50", que é a mesma mentira do saldo em R$ 0,00 quando o
   * servidor está fora.
   */
  sugestoes: {
    total: number;
    /** Esperando o próximo resumo. Normal, não é alarme. */
    naFila: number;
    /** Passou da cadência e o resumo não levou. Isto é alarme. */
    atrasadas: number;
    mostrando: number; teto: number;
    itens: SugestaoRecebida[];
  } | null;
  em: string;
}

export const getMonitor = async (): Promise<Monitor> =>
  (await api.get<Monitor>('/monitor')).data;

export const registrarBackup = async (): Promise<RetratoDoBackup> =>
  (await api.post<RetratoDoBackup>('/monitor/backup')).data;

/** Responde só sobre quem pergunta — ver a nota em `routes/monitor.ts`. */
export const getMonitorAccess = async (): Promise<boolean> => {
  try {
    return (await api.get<{ admin: boolean }>('/monitor/acesso')).data.admin;
  } catch {
    return false;
  }
};

/* ── Avisos no celular (push) ─────────────────────────────────────────────
 *
 * A chave pública vem do SERVIDOR e não de uma variável de build: assim as
 * duas pontas leem a mesma fonte, e trocar a chave não exige uma build nova do
 * frontend — ver a nota em `routes/push.ts`.
 */

export const getChavePush = async (): Promise<string | null> =>
  (await api.get<{ chave: string | null }>('/push/chave')).data.chave;

export const salvarInscricaoPush = async (inscricao: unknown): Promise<void> => {
  await api.post('/push', inscricao);
};

export const removerInscricaoPush = async (endpoint?: string): Promise<void> => {
  await api.delete('/push', endpoint ? { data: { endpoint } } : undefined);
};

/**
 * Manda um aviso de teste para os aparelhos dela, agora.
 *
 * Chamado logo depois de ligar os avisos: se a notificação aparece, a corrente
 * inteira está de pé. Se não, ela descobre no segundo seguinte em vez de daqui
 * a uma semana, quando o primeiro alerta de verdade não chegar.
 */
export const enviarPushDeTeste = async (): Promise<boolean> => {
  try {
    return (await api.post<{ enviado: boolean }>('/push/teste')).data.enviado;
  } catch {
    return false;
  }
};

/**
 * O recibo do clique na notificação.
 *
 * A chave `<tipo>.<referência>.<período>` vem da URL que o push abriu; quem a
 * captura e guarda é `utils/reciboDoAviso.ts`. Aqui é só o transporte.
 */
export const registrarCliqueDeAviso = async (aviso: string): Promise<boolean> => {
  const { data } = await api.post<{ registrado: boolean }>('/avisos/clicado', { aviso });
  return data.registrado;
};

export type OrigemDoHorario = 'escolhida' | 'aprendida' | 'padrao';

export interface HorarioDoAviso {
  hora: number;
  origem: OrigemDoHorario;
  /** Quantos lançamentos sustentam o horário aprendido. `null` quando ela escolheu. */
  amostra: number | null;
}

export const getHorarioDoAviso = async (): Promise<HorarioDoAviso> =>
  (await api.get<HorarioDoAviso>('/avisos/horario')).data;

/** `null` devolve o horário ao automático. */
export const setHorarioDoAviso = async (hora: number | null): Promise<void> => {
  await api.put('/avisos/horario', { hora });
};

/* ── Compartilhar o relatório mensal ─────────────────────────────────────── */

export type EscopoDoRelatorio = 'resumo' | 'completo';

export type StatusDoCompartilhamento =
  'pendente' | 'aceito' | 'recusado' | 'revogado' | 'saiu';

export interface PessoaAutorizada {
  id: string;
  email: string;
  label: string | null;
  escopo: EscopoDoRelatorio;
  status: StatusDoCompartilhamento;
  convidadoEm: string | null;
  respondidoEm: string | null;
  encerradoEm: string | null;
  ultimoEnvioEm: string | null;
}

export interface TextoDoEscopo {
  titulo: string;
  inclui: string;
  naoInclui: string;
}

/**
 * A lista, e os TEXTOS de cada escopo.
 *
 * Os textos descem do servidor de propósito: são eles que o email de convite
 * promete. Uma cópia aqui divergiria da de lá no primeiro ajuste, e a tela
 * passaria a prometer uma coisa enquanto o convite promete outra — com alguém
 * consentindo com a frase errada.
 */
export const getCompartilhamentos = async (): Promise<{
  pessoas: PessoaAutorizada[];
  escopos: Record<EscopoDoRelatorio, TextoDoEscopo>;
}> => (await api.get('/compartilhamentos')).data;

export const convidarParaRelatorio = async (
  email: string,
  escopo: EscopoDoRelatorio,
  label?: string,
): Promise<PessoaAutorizada> =>
  (await api.post('/compartilhamentos', { email, escopo, label })).data;

export const mudarEscopoDoRelatorio = async (
  id: string,
  escopo: EscopoDoRelatorio,
): Promise<PessoaAutorizada> =>
  (await api.patch(`/compartilhamentos/${id}`, { escopo })).data;

export const removerDoRelatorio = async (id: string): Promise<void> => {
  await api.delete(`/compartilhamentos/${id}`);
};

/* ── O Finn ──────────────────────────────────────────────────────────────── */

export type TipoDeAlvo = 'calculado' | 'informado' | 'nenhum';

export interface PortaDoFinn {
  id: string;
  rotulo: string;
  tipoDeAlvo: TipoDeAlvo;
  /**
   * O alvo que o servidor propõe, já calculado. `null` quando não há o que
   * propor — porta sem alvo, ou conta sem recorrente nenhuma.
   *
   * Vem PRONTO de propósito: "três meses de contas fixas" é regra de produto,
   * e ela tem um dono só (`services/finn/objetivo.ts`). Multiplicar aqui
   * criaria o segundo dono, que ficaria para trás em silêncio no dia em que a
   * regra mudasse.
   */
  alvoSugerido: number | null;
}

export interface PortaDoFinnResposta {
  precisaPerguntar: boolean;
  portas: PortaDoFinn[];
  contasFixasMensais?: number;
}

/**
 * O modal abre? Quem responde é o SERVIDOR, não o `localStorage`.
 *
 * A pergunta certa não é "este navegador já mostrou?", é "esta pessoa já
 * declarou?" — senão limpar o navegador ou abrir no computador faz o Finn
 * perguntar de novo a quem já respondeu no celular.
 */
export const lerPortaDoFinn = async (): Promise<PortaDoFinnResposta> =>
  (await api.get('/finn/porta')).data;

/** "Depois": fechou a pergunta sem responder. O Finn volta a perguntar em trinta dias. */
export const adiarPerguntaDoFinn = async (): Promise<void> => {
  await api.post('/finn/porta/depois');
};

export const declararObjetivo = async (portaId: string, alvo: number | null) =>
  (await api.post('/finn/objetivo', { portaId, alvo })).data;

export interface MarcadorDoFinn {
  id: string;
  tipo: string;
  texto: string;
  /**
   * Para qual seção do app este marcador leva — ou `null` quando é só aviso.
   *
   * ── Por que um rótulo de seção, e não a ação ─────────────────────────────
   *
   * O painel do site orienta; ele não cria. A regra é dela e está guardada em
   * `advisorSemInsight.test.ts`. O marcador mostra a oferta e leva até a tela
   * onde criar meta sempre esteve; quem executa oferta é o bot do WhatsApp.
   *
   * E mesmo que executasse: a ação carrega id de categoria e valor, e mandá-la
   * para o cliente seria dar a ele um payload executável para devolver
   * modificado.
   */
  destino: string | null;
}

/**
 * O que o Finn tem a dizer agora — ou `null`.
 *
 * Rota barata de propósito: quem DETECTA as situações é o job diário, e o
 * resultado já está gravado. Ver `routes/finn.ts` para o que acontece se
 * alguém trouxer a detecção para a requisição "para ficar mais fresco".
 */
export const lerMarcadorDoFinn = async (): Promise<MarcadorDoFinn | null> =>
  (await api.get('/finn/marcador')).data.marcador ?? null;

/** Ela leu. Idempotente no servidor: repetir não move a data. */
export const marcarMarcadorVisto = async (id: string): Promise<void> => {
  await api.post(`/finn/marcador/${id}/visto`);
};


/*
 * ── Open Finance ───────────────────────────────────────────────────────────
 *
 * Desenho em `docs/designs/open-finance.md`. O método vem da conta do banco, a
 * categoria o servidor descobre sozinho, e cada pessoa escolhe entre lançar
 * automático ou ser avisada.
 */

export interface ConexaoDoBanco {
  id: string;
  banco: string;
  /** Como o provedor a vê: UPDATED, LOGIN_ERROR, OUTDATED… */
  status: string;
  sincronizadaEm: string | null;
  /** Desde quando o automático está ligado. `null` = modo avisar. */
  automaticoDesde: string | null;
  /** O período do extrato já lido ('AAAA-MM-DD'). Ausente ou `null` = nunca leu. */
  periodo?: { desde: string; ate: string } | null;
}

/** Uma conta do banco, e o método do FinTracking que ela é. */
export interface ContaDoBanco {
  id: string;
  conexaoId: string;
  tipo: 'BANK' | 'CREDIT';
  nome: string;
  diaDeVencimento: number | null;
  paymentMethodId: string | null;
  metodo: string | null;
  /** O saldo que o banco informou na última leitura. Só na conta corrente. */
  saldo?: number | null;
  /** Quando esse saldo foi lido, ISO. */
  saldoEm?: string | null;
}

/** Uma fatura a que o movimento pode ser vinculado. Ver `pagamentoDeFatura.ts`, no backend. */
export interface OpcaoDeFatura {
  metodoId: string;
  nome: string;
  /** 'AAAA-MM' do vencimento. */
  mes: string;
  total: number;
  pago: number;
  /** O que ainda falta depois deste movimento. */
  falta: number;
  /** Quanto o pagamento passa do que está lançado no cartão. */
  sobra: number;
  tipo: 'exata' | 'parcial' | 'maior';
  jaPaga: boolean;
}

export interface SugestaoDeFatura {
  /** Foi o BANCO que disse que é pagamento de fatura. */
  marcada: boolean;
  /** `unica`: a primeira opção é a sugestão. `ambigua`: ela escolhe. */
  certeza: 'unica' | 'ambigua';
  opcoes: OpcaoDeFatura[];
}

/** Um pagamento já vinculado a uma fatura. */
export interface VinculoDeFatura {
  id: string;
  descricao: string;
  valor: number;
  data: string;
  metodoId: string;
  cartao: string;
  mes: string;
  automatico: boolean;
  total: number;
  pago: number;
  paga: boolean;
}

export interface MovimentoDoBanco {
  id: string;
  /** De qual banco conectado veio. É o que separa as abas. */
  conexaoId: string;
  contaId: string;
  conta: 'BANK' | 'CREDIT';
  sentido: 'saida' | 'entrada';
  valor: number;
  /** 'AAAA-MM-DD'. No cartão, é o dia da compra. */
  data: string;
  /** Como o BANCO chama. O nome que vai para o lançamento está em `sugestao.nome`. */
  descricao: string;
  parcela?: { numero: number; total: number };
  /** 'AAAA-MM' da fatura em que a parcela cai, dito pelo banco. */
  mesDaFatura?: string;
  /** A compra ainda está pendente no banco: pode mudar de valor ou sumir. */
  pendente?: boolean;
  estado: 'novo' | 'provavel' | 'casado' | 'aceito';
  /** O id do lançamento com que casou. */
  casadoCom: string | null;
  /** `true` quando entrou sozinho, sem ela tocar. */
  automatico: boolean;
  /** O método da conta do banco. `null` = a conta ainda não foi ligada. */
  metodo: string | null;
  /**
   * O que o app faria. `sabe: false` é o único caso em que ele pergunta a
   * categoria. `origem` diz de onde tirou: regra dela, histórico dela, ou o
   * palpite do banco.
   */
  sugestao: {
    categoryId: string | null;
    nome: string;
    origem: 'regra' | 'historico' | 'banco' | null;
    sabe: boolean;
  };
  /** Parece o pagamento da fatura de um cartão dela? Só em quem espera por ela. */
  fatura?: SugestaoDeFatura | null;
}

export interface CaixaDoBancoDados {
  conexoes: ConexaoDoBanco[];
  contas: ContaDoBanco[];
  pendentes: MovimentoDoBanco[];
  casados: MovimentoDoBanco[];
  lancados: MovimentoDoBanco[];
  /** As categorias que resolveram as sugestões. É delas que a tela tira os nomes. */
  categorias?: { id: string; name: string }[];
  /** As compras que o banco desfez, até ela dispensar o aviso. */
  estornados?: EstornadoDoBanco[];
  /** Parceladas lançadas antes do mês em que o banco cobra. */
  adiantadas?: ParceladaAdiantada[];
  /** Os pagamentos já vinculados a uma fatura, para ver e desfazer. */
  faturas?: VinculoDeFatura[];
  /** Os cartões com fatura, para escolher à mão. Vazio = o vínculo não está ligado. */
  cartoesDeFatura?: { id: string; nome: string }[];
  /** O servidor libera as ferramentas de suporte (raio-x, retrato) para esta conta. */
  suporte?: boolean;
  /** A situação do acesso de quem está logada: a dona da chave, ou uma pessoa liberada. Vem junto para a tela nunca ficar sem saber. */
  acesso?: AcessoAoBanco;
}

/** Uma parcelada lançada num mês anterior ao que o banco cobra. */
export interface ParceladaAdiantada {
  id: string; conexaoId: string; descricao: string; valor: number; parcela: string; meses: number;
  /** O mês ('AAAA-MM') em que a parcela de agora cai no app, e em que o banco cobra. */
  noAppEm?: string; noBancoEm?: string;
  lancadoEm: string; certoEm: string;
}

/** Empurra as parceladas adiantadas de um banco para o mês em que ele cobra. */
export const acertarParceladasDoBanco = async (conexaoId: string): Promise<{ acertadas: number }> =>
  (await api.post('/banco/parceladas/acertar', { conexaoId })).data;

/** Uma compra que o banco estornou. */
export interface EstornadoDoBanco {
  id: string;
  conexaoId: string;
  sentido: 'saida' | 'entrada';
  valor: number;
  data: string;
  descricao: string;
  parcela?: { numero: number; total: number };
  /** O movimento que ela lançou e o banco desfez. `null` = não tinha lançado. */
  lancadoComo: string | null;
}

/**
 * A situação do acesso ao Open Finance de quem está logada — ver
 * `backend/src/services/banco/acessoPago.ts`.
 */
export type EstadoDoAcessoAoBanco = 'dona' | 'sem' | 'liberado' | 'vencido';

export interface AcessoAoBanco {
  estado: EstadoDoAcessoAoBanco;
  liberadoAte: string | null;
  diasRestantes: number | null;
  precoCentavos: number;
  maxBancos: number;
  /** O banco extra do Completo, contratado em "Meu plano". */
  precoDoBancoExtraCentavos?: number;
  /** Quantos bancos cabem no total (o primeiro mais os extras). */
  maxBancosNoTotal?: number;
  /** Tem plano, mas ele não inclui banco (Essencial ou Anual). */
  planoSemBanco?: boolean;
}

export const getAcessoAoBanco = async (): Promise<AcessoAoBanco> =>
  (await api.get<AcessoAoBanco>('/banco/acesso')).data;

/** Abre a sessão de conexão dela no provedor: devolve o endereço da tela onde ela escolhe o banco e consente. */
export const iniciarConexaoDoBanco = async (): Promise<{ url: string }> =>
  (await api.post<{ url: string }>('/banco/conexao/iniciar')).data;

/** Ela voltou do provedor com o id da sessão: o servidor confere de quem é e guarda o banco. */
export const confirmarConexaoDoBanco = async (sessao: string): Promise<{ id: string; banco: string }> =>
  (await api.post<{ conexao: { id: string; banco: string } }>('/banco/conexao/confirmar', { sessao })).data.conexao;

/** O formato das conexões no provedor, sem o conteúdo. Ferramenta de quem cuida do app. */
export const getFormatoDasConexoes = async (): Promise<unknown> =>
  (await api.get('/banco/formato-das-conexoes')).data;

/* ── O acesso, visto do Monitor ─────────────────────────────────────────── */

export interface PessoaNoAcesso {
  userId: string; email: string | null; nome: string | null;
  liberadoAte: string | null; diasRestantes: number | null; bancos: number;
  maxBancos: number;
}

/** Viva no provedor, carimbada para uma pessoa, e que o app não guarda: custa e ninguém vê. */
export interface ConexaoOrfa {
  conexaoId: string; banco: string; userId: string; email: string | null; nome: string | null;
}

export interface PainelDeAcessoAoBanco {
  /** Quem tem o Completo (ou algum banco conectado). */
  liberadas: PessoaNoAcesso[];
  /** `null` = o provedor não respondeu, e a lista não pôde ser conferida. */
  orfas?: ConexaoOrfa[] | null;
}

export const getPainelDeAcessoAoBanco = async (): Promise<PainelDeAcessoAoBanco> =>
  (await api.get<PainelDeAcessoAoBanco>('/monitor/banco-acesso')).data;

/** Desliga os bancos dela no provedor, sem mexer no plano. */
export const desconectarBancosDe = async (userId: string): Promise<void> => {
  await api.post(`/monitor/banco-acesso/${encodeURIComponent(userId)}/desconectar`);
};

export const desligarConexaoOrfa = async (conexaoId: string): Promise<void> => {
  await api.post(`/monitor/banco-acesso/orfas/${encodeURIComponent(conexaoId)}/desligar`);
};

/** `disponivel: false` = para esta conta a feature não existe; a tela não mostra o menu. */
export const getEstadoDoBanco = async (): Promise<{
  disponivel: boolean; conexoes?: ConexaoDoBanco[];
  /** O servidor ainda está subindo e não sabe responder: pergunte de novo. */
  iniciando?: boolean;
  /** Quantos movimentos esperam por ela. É o contador do menu. */
  esperando?: number;
  /** Ela ainda não pode usar, mas pode PEDIR: o menu aparece com a oferta. */
  oferta?: boolean;
}> =>
  (await api.get('/banco/estado')).data;

/** Traz os bancos conectados no provedor e faz a primeira leitura. */
export const buscarConexoesDoBanco = async (): Promise<{
  novos: number; conexoesLidas: ConexaoDoBanco[]; falhas?: { banco: string; motivo: string }[];
}> =>
  (await api.post('/banco/conexoes/atualizar')).data;

/**
 * Lê UM banco. A tela chama uma vez por banco: ler todos numa requisição só
 * passa do tempo que o servidor dá a cada uma.
 */
export const sincronizarBanco = async (conexaoId: string, releitura = false): Promise<{
  lidos: number; novos: number; lancados: number;
  /** Os bancos que não foram lidos, e por quê. */
  falhas?: { banco: string; motivo: string }[];
  /** A leitura passou do tempo de espera e CONTINUA no servidor. Não é erro. */
  emAndamento?: boolean;
}> => {
  const r = (await api.post('/banco/sincronizar', { conexaoId, ...(releitura ? { releitura: true } : {}) })).data;
  return r?.emAndamento ? { lidos: 0, novos: 0, lancados: 0, emAndamento: true } : r;
};

export const getCaixaDoBanco = async (): Promise<CaixaDoBancoDados> =>
  (await api.get<CaixaDoBancoDados>('/banco/caixa')).data;

/** Liga a conta do banco a um método que ela já tem; sem `paymentMethodId`, cadastra um. */
export const ligarContaDoBanco = async (
  contaId: string, escolha: { paymentMethodId?: string | null; nome?: string },
): Promise<void> => {
  await api.post(`/banco/contas/${contaId}/metodo`, escolha);
};

export const definirModoDoBanco = async (conexaoId: string, automatico: boolean): Promise<void> => {
  await api.put(`/banco/conexoes/${conexaoId}/modo`, { automatico });
};

/**
 * Lança um movimento. Campo AUSENTE = vale o que o app sugeriu; mandar
 * `categoryId: null` é tirar a categoria de propósito.
 */
export const aceitarMovimentoDoBanco = async (
  id: string, escolha: { categoryId?: string | null; nome?: string } = {},
): Promise<{ transactionId: string }> =>
  (await api.post(`/banco/movimentos/${id}/aceitar`, escolha)).data;

/* ── Suporte do Open Finance ─────────────────────────────────────────────── */

export type TipoDePedidoDeSuporte = 'faltou' | 'parcelada' | 'outro';

/** O que a pessoa vê dos próprios pedidos: nada do diagnóstico. */
export interface MeuPedidoDeSuporte {
  id: string;
  banco: string;
  tipo: TipoDePedidoDeSuporte;
  criadoEm: string;
}

/** `disponivel: false` = o formulário não existe para esta conta; a tela só não mostra a linha. */
export const getSuporteDoBanco = async (): Promise<{
  disponivel: boolean; email?: string | null; pedidos: MeuPedidoDeSuporte[];
}> => (await api.get('/banco/suporte')).data;

/**
 * Envia um pedido de suporte. O diagnóstico é gerado pelo servidor depois da
 * resposta: a tela não espera por ele nem o recebe.
 */
export const enviarPedidoDeSuporte = async (pedido: {
  conexaoId: string; tipo: TipoDePedidoDeSuporte; mensagem: string; valor?: string;
}): Promise<{ id: string; criadoEm: string }> => (await api.post('/banco/suporte', pedido)).data;

/** Um pedido na fila de quem atende. Só o Monitor recebe isto. */
export interface PedidoDeSuporte {
  id: string;
  email: string | null;
  nome: string | null;
  banco: string;
  tipo: TipoDePedidoDeSuporte;
  mensagem: string;
  valorCentavos: number | null;
  criadoEm: string;
  /** `null` = a foto ainda não foi tirada. */
  diagnosticoEm: string | null;
  diagnostico: unknown | null;
}

export const getFilaDeSuporte = async (): Promise<PedidoDeSuporte[]> =>
  (await api.get<{ pedidos: PedidoDeSuporte[] }>('/monitor/suporte')).data.pedidos;

export const resolverPedidoDeSuporte = async (id: string): Promise<void> => {
  await api.post(`/monitor/suporte/${id}/resolver`);
};

export const regerarDiagnosticoDeSuporte = async (id: string): Promise<void> => {
  await api.post(`/monitor/suporte/${id}/diagnostico`);
};

/**
 * O que a leitura fez com cada tipo de linha de cada conta — sem descrição,
 * valor nem data. Ferramenta de suporte, só para admin.
 */
export const getRaioXDoBanco = async (valor?: string, conexaoId?: string): Promise<unknown> =>
  (await api.get('/banco/raio-x', { params: { ...(valor ? { valor } : {}), ...(conexaoId ? { conexaoId } : {}) } })).data;

/** Ela viu o aviso de estorno: some da tela. */
export const dispensarEstornoDoBanco = async (id: string): Promise<void> => {
  await api.post(`/banco/estornos/${id}/dispensar`);
};

/** O endereço, no provedor, onde ela conecta mais um banco. */
export const getLinkParaConectarBanco = async (): Promise<string> =>
  (await api.get<{ url: string }>('/banco/conectar')).data.url;

/**
 * Como o cartão manda as parcelas — sem descrição nem valor. É para ela copiar
 * e mandar a quem cuida do app quando uma parcelada antiga não aparece.
 */
export const getRetratoDoCartao = async (): Promise<unknown> =>
  (await api.get('/banco/retrato-do-cartao')).data;

/**
 * Lança vários de uma vez. Uma chamada só por remessa, para não gastar o teto
 * de escrita num toque.
 *
 * `falhas` traz o motivo de cada um que não entrou. `restantes` são os que o
 * servidor não teve tempo de tentar: quem chama reenvia.
 */
export const aceitarMovimentosDoBancoEmLote = async (
  itens: { id: string; categoryId?: string | null; nome?: string }[],
): Promise<{ lancados: number; falhas: { id: string; erro: string }[]; restantes?: string[] }> =>
  (await api.post('/banco/movimentos/aceitar-lote', { itens })).data;

/** "É o mesmo": é um lançamento que ela já fez. */
export const confirmarMesmoDoBanco = async (id: string): Promise<void> => {
  await api.post(`/banco/movimentos/${id}/mesmo`);
};

export const ignorarMovimentoDoBanco = async (id: string): Promise<void> => {
  await api.post(`/banco/movimentos/${id}/ignorar`);
};

/** "Não é o mesmo lançamento": o movimento volta a ser novo. */
export const separarMovimentoDoBanco = async (id: string): Promise<void> => {
  await api.post(`/banco/movimentos/${id}/separar`);
};

/** Corrige um que entrou sozinho — e o app aprende para os próximos. */
export const corrigirMovimentoDoBanco = async (
  id: string, escolha: { categoryId?: string | null; nome?: string },
): Promise<void> => {
  await api.post(`/banco/movimentos/${id}/corrigir`, escolha);
};

/** "É o pagamento da fatura deste cartão": não cria lançamento; se o valor cobre, a fatura fica paga. */
export const vincularFaturaDoBanco = async (
  id: string, metodoId: string, mes: string,
): Promise<{ paga: boolean; pago: number; total: number }> =>
  (await api.post(`/banco/movimentos/${id}/fatura`, { metodoId, mes })).data;

/** Desfaz o vínculo: o movimento volta para os novos. */
export const desvincularFaturaDoBanco = async (id: string): Promise<void> => {
  await api.delete(`/banco/movimentos/${id}/fatura`);
};

export const desfazerMovimentoDoBanco = async (id: string): Promise<void> => {
  await api.post(`/banco/movimentos/${id}/desfazer`);
};

/** Um empréstimo que o banco mostra, pronto para virar dívida. */
export interface SugestaoDeDividaDoBanco {
  idExterno: string;
  nome: string;
  tipo: DebtType;
  parcelas: number;
  valorDaParcela: number;
  primeiraParcela: string;
  /** `true` só para consignado. `null` = a tela pergunta. */
  descontaEmFolha: boolean | null;
  taxaMensal: number | null;
  /** O que o BANCO diz que ela deve hoje. */
  saldoDevedorNoBanco: number | null;
  jaCadastrada: boolean;
}

/** `naoLidos`: os bancos que não responderam a tempo. Deles não se sabe se há empréstimo. */
export const getEmprestimosDoBanco = async (): Promise<{ sugestoes: SugestaoDeDividaDoBanco[]; naoLidos: string[] }> => {
  const { data } = await api.get<{ sugestoes: SugestaoDeDividaDoBanco[]; naoLidos?: string[] }>('/banco/emprestimos');
  return { sugestoes: data.sugestoes ?? [], naoLidos: data.naoLidos ?? [] };
};

export const desconectarBanco = async (conexaoId: string): Promise<void> => {
  await api.delete(`/banco/conexoes/${conexaoId}`);
};

/* ── Newsletter diária ───────────────────────────────────────────────────── */

export interface PreferenciasDaNewsletter {
  /** `false` enquanto o esquema não subiu em produção: a tela não oferece. */
  disponivel: boolean;
  ativa: boolean;
  temas: string[];
  opcoes: { id: string; nome: string }[];
}

export const getNewsletter = async (): Promise<PreferenciasDaNewsletter> =>
  (await api.get<PreferenciasDaNewsletter>('/newsletter')).data;

export const setNewsletter = async (ativa: boolean, temas: string[]): Promise<void> => {
  await api.put('/newsletter', { ativa, temas });
};


/* ── Os planos do app (20/10) — docs/designs/precificacao.md ───────────── */

export type IdDoPlano = 'essencial' | 'essencial_anual' | 'completo';
export type EstadoDoPlano =
  | 'desligado' | 'nao-sei' | 'dona' | 'isenta'
  | 'sem' | 'pedido' | 'liberado' | 'vencido' | 'encerrado';

export interface DadosDoPlano {
  id: IdDoPlano; nome: string; precoCentavos: number; dias: number; comBanco: boolean;
  /** Bancos incluídos (28/10: 3 no Completo, 0 no Essencial e no Anual). */
  bancos: number;
  /** Aceita banco extra? Só o Completo. */
  aceitaExtra: boolean;
}

export interface SituacaoDoPlano {
  cobrando: boolean;
  estado: EstadoDoPlano;
  plano: IdDoPlano | null;
  liberadoAte: string | null;
  diasRestantes: number | null;
  podePagar: boolean;
  maxBancos: number;
  planos: DadosDoPlano[];
  bancoExtraCentavos: number;
  garantia: string;
  /** Com o Asaas ligado, paga-se lá (Pix ou cartão); sem ele, o pagamento não está no ar. */
  comAsaas?: boolean;
  resultado?: 'pedido' | 'troca-no-vencimento' | 'mesmo-plano';
}

/** Os estados que usam o app — o mesmo conjunto de `daAcessoAoApp` no servidor. */
export const PASSAM_PELO_PLANO: readonly EstadoDoPlano[] = ['desligado', 'nao-sei', 'dona', 'isenta', 'liberado', 'vencido'];

export const getPlano = async (): Promise<SituacaoDoPlano> => (await api.get<SituacaoDoPlano>('/planos')).data;

export const escolherPlano = async (plano: IdDoPlano): Promise<SituacaoDoPlano> =>
  (await api.post<SituacaoDoPlano>('/planos/escolher', { plano })).data;

/** O cupom (27/10): libera o plano dele na hora. Recusado, o erro traz a frase para a tela. */
export const usarCupom = async (codigo: string): Promise<SituacaoDoPlano> =>
  (await api.post<SituacaoDoPlano>('/planos/cupom', { codigo })).data;

export type Devolucao =
  | { tipo: 'obrigatoria'; centavos: number; dias: number }
  | { tipo: 'escolha'; metadeCentavos: number; tudoCentavos: number; dias: number }
  | { tipo: 'fora'; dias: number };

export interface PessoaComPlano {
  userId: string; email: string | null; nome: string | null;
  estado: 'pedido' | 'liberado' | 'encerrado';
  plano: IdDoPlano; planoProximo: IdDoPlano | null;
  liberadoAte: string | null; diasRestantes: number | null;
  fundador: boolean; maxBancos: number; bancos: number;
}

export interface PainelDosPlanos {
  disponivel: boolean; cobrando: boolean;
  completos: number; completosParaMigrar: number;
  fundadores: number; vagasDeFundador: number;
  pessoas: PessoaComPlano[];
}

export interface PagamentoDoPlano {
  id: number; plano: IdDoPlano; valorCentavos: number; pagoEm: string; dias: number;
  /** 'manual': o Pix fora do Asaas, de antes de 23/10. Só no histórico. */
  primeiro: boolean; origem: 'asaas' | 'manual'; referencia: string | null; chavePix: string | null;
  devolvidoEm: string | null; valorDevolvidoCentavos: number | null; devolucao: Devolucao;
  /** 'troca': a diferença paga para subir de plano (22/10). */
  tipo?: 'periodo' | 'troca';
  /** Cancelou do 8º ao 30º dia e pediu a devolução: ela decide. */
  devolucaoPedidaEm?: string | null;
}

export const getPainelDosPlanos = async (): Promise<PainelDosPlanos> =>
  (await api.get<PainelDosPlanos>('/monitor/planos')).data;

export const getPagamentosDoPlano = async (userId: string): Promise<PagamentoDoPlano[]> =>
  (await api.get<{ pagamentos: PagamentoDoPlano[] }>(`/monitor/planos/${encodeURIComponent(userId)}/pagamentos`)).data.pagamentos;

/** `aviso`: o estorno saiu, mas o banco não desconectou no provedor. */
export const devolverPagamento = async (id: number, valorCentavos: number): Promise<{ encerra: boolean; aviso?: string }> =>
  (await api.post<{ ok: true; encerra: boolean; aviso?: string }>(`/monitor/planos/pagamentos/${id}/devolver`, { valorCentavos })).data;

export type ResultadoDeAssinar =
  | { tipo: 'link'; link: string }
  | { tipo: 'troca-no-vencimento' }
  | { tipo: 'mesmo-plano' };

/** Assina pelo Asaas. O CPF vai para o Asaas e não fica guardado no app. */
export const assinarPlano = async (e: { plano: IdDoPlano; forma: 'pix' | 'cartao'; cpf: string }): Promise<ResultadoDeAssinar> =>
  (await api.post<ResultadoDeAssinar>('/planos/assinar', e)).data;

/* ── "Meu plano" (22/10): ver, trocar e cancelar ───────────────────────── */

export interface MeuPlano {
  estado: 'pedido' | 'liberado' | 'encerrado';
  plano: IdDoPlano; maxBancos: number; bancosConectados: number;
  liberadoAte: string | null; diasRestantes: number | null; fundador: boolean;
  planoProximo: IdDoPlano | null; maxBancosProximo: number | null; canceladoEm: string | null;
  ultimoPagamento: { id: number; valorCentavos: number; pagoEm: string; primeiro: boolean; origem: 'asaas' | 'manual' } | null;
}

export const getMeuPlano = async (): Promise<{ plano: MeuPlano | null; podeTrocar: boolean }> =>
  (await api.get<{ plano: MeuPlano | null; podeTrocar: boolean }>('/planos/meu')).data;

export type ResultadoDaTroca =
  | { tipo: 'link'; link: string; valorCentavos: number }
  | { tipo: 'subiu' }
  | { tipo: 'no-vencimento'; quando: string | null }
  | { tipo: 'mesmo-plano' }
  | { tipo: 'recusado'; motivo: 'sem-plano' | 'cancelado' | 'tem-mais-conectado' | 'sem-asaas' | 'renovar-antes' };

export const trocarDePlano = async (plano: IdDoPlano, bancos: number): Promise<ResultadoDaTroca> =>
  (await api.post<ResultadoDaTroca>('/planos/trocar', { plano, bancos })).data;

export type ResultadoDoCancelamento =
  | { tipo: 'estornado'; centavos: number }
  | { tipo: 'pedido-de-devolucao'; valeAte: string | null }
  | { tipo: 'cancelado'; valeAte: string | null }
  | { tipo: 'recusado'; motivo: 'sem-plano' | 'ja-cancelado' };

export const cancelarPlano = async (): Promise<ResultadoDoCancelamento> =>
  (await api.post<ResultadoDoCancelamento>('/planos/cancelar')).data;

export type PreviaDaTroca =
  | { tipo: 'subida'; valorCentavos: number; cobra: boolean; ate: string; precoDepoisCentavos: number }
  | { tipo: 'descida'; quando: string; precoDepoisCentavos: number }
  | { tipo: 'mesmo-plano' }
  | { tipo: 'recusado'; motivo: 'sem-plano' | 'cancelado' | 'tem-mais-conectado' | 'renovar-antes' };

/** O que a troca vai fazer, calculado no servidor pela mesma conta que cobra. */
export const getPreviaDaTroca = async (plano: IdDoPlano, bancos: number): Promise<PreviaDaTroca> =>
  (await api.get<PreviaDaTroca>('/planos/trocar/previa', { params: { plano, bancos } })).data;

/* ── Comece por aqui (22/10) — docs/designs/comece-por-aqui.md ──────────── */

export interface EstadoDoComece {
  feitos: FeitosDoComece;
  /** `null` = o servidor não guarda (colunas ainda não criadas): vale o navegador. */
  marcas: { aberto: boolean; dispensado: boolean } | null;
}

export const getComeceAqui = async (): Promise<EstadoDoComece> =>
  (await api.get<EstadoDoComece>('/comece-aqui')).data;

export const marcarComeceAberto = async (): Promise<void> => { await api.post('/comece-aqui/aberto'); };

export const dispensarComeceAqui = async (): Promise<void> => { await api.post('/comece-aqui/dispensar'); };
